import type { Book } from '@booknest/shared';

/**
 * Mapping genre → classes CSS de texture.
 * Suit exactement la recette `docs/BOOK_SPINE_RECIPE.md` §7.
 *
 * La valeur est une chaîne de classes prête à être injectée dans `className`.
 * Pour `classique`, la chaîne contient **deux classes** : `tex-leather-aged`
 * pour la matière et `gold-band` pour activer les bandes dorées.
 */
export const GENRE_TEXTURE: Record<string, string> = {
  romantasy: 'tex-leather',
  romance: 'tex-glossy',
  fantasy: 'tex-cloth',
  thriller: 'tex-glossy',
  'feel-good': 'tex-paper',
  classique: 'tex-leather-aged gold-band',
  'litterature-fr': 'tex-cloth',
  fiction: 'tex-paper',
  other: 'tex-paper',
};

/**
 * Palette de tranches par genre — 3 variantes (c1, c2) par genre.
 * Couleurs strictement alignées sur `booknest-mockup-v2.html` (recette §8).
 */
export const GENRE_COLORS: Record<string, ReadonlyArray<readonly [string, string]>> = {
  romantasy: [
    ['#8B2D3D', '#3D0F18'],
    ['#A5374A', '#4D1421'],
    ['#6B1E2C', '#2D0810'],
  ],
  romance: [
    ['#D49B95', '#6B3F3F'],
    ['#C97B7B', '#5C2C2C'],
    ['#E0A8A8', '#704848'],
  ],
  fantasy: [
    ['#3D4D6B', '#1F2A40'],
    ['#5A4D7B', '#2F234A'],
    ['#2A3852', '#15203A'],
  ],
  thriller: [
    ['#1F2225', '#000000'],
    ['#2A2D30', '#0A0A0C'],
    ['#3D3038', '#1A1518'],
  ],
  'feel-good': [
    ['#D4A574', '#8B6A47'],
    ['#E4C994', '#A88A5C'],
    ['#B89265', '#7A5A38'],
  ],
  classique: [
    ['#5C2D1E', '#2A0F08'],
    ['#6B3828', '#3D1808'],
    ['#4A2418', '#1F0A05'],
  ],
  'litterature-fr': [
    ['#3D5C40', '#1A2F1F'],
    ['#4A6B4A', '#1F3A24'],
    ['#2D4530', '#0F1F14'],
  ],
  fiction: [
    ['#7A5C8B', '#3D2A4F'],
    ['#5C4569', '#2F1F40'],
    ['#8B6BA0', '#4A3559'],
  ],
  other: [
    ['#9C8770', '#5C4530'],
  ],
};

/**
 * Hash d'un id de livre — recette §12 : somme des charCodes.
 * Volontairement simple (et reproductible côté maquette).
 */
function idHash(bookId: string): number {
  let acc = 0;
  for (let i = 0; i < bookId.length; i += 1) {
    acc += bookId.charCodeAt(i);
  }
  return acc;
}

/**
 * Choisit la paire (c1, c2) d'un livre — recette §8.
 * Même livre = mêmes couleurs ; livres voisins du même genre = nuances variées.
 */
export function pickColors(
  book: Pick<Book, 'id' | 'genre'>,
): readonly [string, string] {
  const genre = book.genre ?? 'other';
  const palette = GENRE_COLORS[genre] ?? GENRE_COLORS['other']!;
  const h = idHash(book.id);
  return palette[h % palette.length] ?? palette[0]!;
}

/**
 * Retourne la chaîne de classes texture pour ce livre (ex : `'tex-leather-aged gold-band'`).
 * À injecter telle quelle dans le `className` du `.book-spine`.
 */
export function pickTexture(book: Pick<Book, 'genre'>): string {
  const genre = book.genre ?? 'other';
  return GENRE_TEXTURE[genre] ?? 'tex-paper';
}

/**
 * Pour les éléments hors `.book-spine` (cover face, trending) — on ne veut que
 * la classe de texture, pas `gold-band` (qui n'a de sens que sur les bandes
 * d'un dos de livre).
 */
export function pickTextureBase(book: Pick<Book, 'genre'>): string {
  return pickTexture(book).split(' ')[0] ?? 'tex-paper';
}

export type Density = 'comfy' | 'compact';

/**
 * Ratios de dimensions d'une tranche (0..1) — recette §4 et §12.
 *
 * À injecter dans des `calc(var(--spine-min-*) + (var(--spine-max-*) - var(--spine-min-*)) * R)`.
 * Les CSS vars `--spine-*` changent automatiquement quand `.density-compact`
 * est appliquée sur un ancêtre → toggle de densité sans re-render React.
 */
export function spineRatios(bookId: string): {
  readonly heightRatio: number;
  readonly widthRatio: number;
} {
  const seed = idHash(bookId);
  return {
    heightRatio: ((seed * 7919) % 100) / 100,
    widthRatio: ((seed * 17) % 100) / 100,
  };
}

/** Largeur moyenne d'une tranche (+ gap) pour calculer la capacité d'un shelf. */
export function averageSpineWidth(density: Density): number {
  // Comfy : moyenne 30 px + gap 1 = 31 ; compact : 14 + 1 = 15
  return density === 'compact' ? 15 : 31;
}

const DECO_KINDS = [
  '🪴',
  '🕯️',
  '☕',
  '🐈',
  '🖼️',
  '🌿',
  '⭐',
  '🍄',
  '🌷',
  '🏮',
] as const;

export type ShelfDecoKind = (typeof DECO_KINDS)[number];

export type DecoSize = 'sm' | 'md';

export interface DecoSlot {
  readonly atIndex: number;
  readonly kind: ShelfDecoKind;
  readonly size: DecoSize;
}

/**
 * Distribue des décorations dans une rangée — algo maquette v2 :
 *  - rangée d'index pair : 1 déco au MILIEU (md). Si > 5 livres → + 1 à la FIN (sm).
 *  - rangée d'index impair : 1 déco à la FIN (md).
 *  - rangée vide : aucune déco.
 */
export function decorationSlots(
  count: number,
  shelfIndex: number,
): ReadonlyArray<DecoSlot> {
  if (count === 0) return [];
  const slots: DecoSlot[] = [];
  if (shelfIndex % 2 === 0) {
    const middle = Math.ceil(count / 2);
    slots.push({
      atIndex: middle,
      kind: DECO_KINDS[shelfIndex % DECO_KINDS.length]!,
      size: 'md',
    });
    if (count > 5) {
      slots.push({
        atIndex: count,
        kind: DECO_KINDS[(shelfIndex + 3) % DECO_KINDS.length]!,
        size: 'sm',
      });
    }
  } else {
    slots.push({
      atIndex: count,
      kind: DECO_KINDS[(shelfIndex + 1) % DECO_KINDS.length]!,
      size: 'md',
    });
  }
  return slots;
}
